import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Menu, Modal, Select,
  Stack, TextInput, Title, Tooltip,
} from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { DataTable } from 'mantine-datatable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconChevronDown, IconEdit, IconPaperclip, IconPlus, IconSearch } from '@tabler/icons-react'
import {
  COLOR_CONDICION, ESTADOS, ETIQUETA_FICHA, TIPOS_FICHA, esEditableEstado,
  type Ficha, type FichaEntrada, type FichaResumen, type TipoFicha,
} from '@serkat/shared'
import { ErrorApi, api, queryString } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { EstadoBadge } from '../../compartido/componentes/EstadoBadge'
import { PanelDocumento } from '../../compartido/componentes/PanelDocumento'
import { fecha } from '../../compartido/formato'
import { FormularioFicha } from './componentes/FormularioFicha'

const POR_PAGINA = 15
interface Pagina { datos: FichaResumen[]; total: number }

interface Vencida {
  id: string; razonSocial: string; fechaProximaEvaluacion: string; diasVencida: number
}

export function FichasPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [pagina, setPagina] = useState(1)
  const [texto, setTexto] = useState('')
  const [buscar] = useDebouncedValue(texto, 300)
  const [estado, setEstado] = useState<string | null>(null)
  const [tipo, setTipo] = useState<string | null>(null)
  const [editando, setEditando] = useState<Ficha | undefined>()
  const [tipoNueva, setTipoNueva] = useState<TipoFicha>('SELECCION_PROVEEDOR')
  const [abierto, modal] = useDisclosure(false)
  const [panel, setPanel] = useState<FichaResumen | null>(null)

  const filtro = {
    pagina, porPagina: POR_PAGINA,
    buscar: buscar || undefined, estado: estado || undefined, tipo: tipo || undefined,
  }

  const { data, isFetching } = useQuery({
    queryKey: ['fichas', filtro],
    queryFn: () => api.get<Pagina>(`/fichas${queryString(filtro)}`),
    placeholderData: (previo) => previo,
  })

  const { data: vencidas } = useQuery({
    queryKey: ['fichas', 'vencidas'],
    queryFn: () => api.get<Vencida[]>('/fichas/evaluaciones-vencidas'),
  })

  const guardar = useMutation({
    mutationFn: (v: FichaEntrada) =>
      editando ? api.patch(`/fichas/${editando.id}`, v) : api.post('/fichas', v),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Ficha guardada' })
      void qc.invalidateQueries({ queryKey: ['fichas'] })
      modal.close()
    },
    onError: (e) =>
      notifications.show({
        color: 'red', title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const puedeEmitir = usuario?.puedeEmitir ?? false

  async function abrir(fila?: FichaResumen, nueva?: TipoFicha) {
    if (fila) setEditando(await api.get<Ficha>(`/fichas/${fila.id}`))
    else { setEditando(undefined); setTipoNueva(nueva ?? 'SELECCION_PROVEEDOR') }
    modal.open()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Evaluación de proveedores</Title>
        {puedeEmitir && (
          <Menu position="bottom-end">
            <Menu.Target>
              <Button leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={14} />}>
                Nueva ficha
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {TIPOS_FICHA.map((t) => (
                <Menu.Item key={t} onClick={() => void abrir(undefined, t)}>
                  {ETIQUETA_FICHA[t]}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {(vencidas?.length ?? 0) > 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
          title="Reevaluaciones vencidas">
          {vencidas!.map((v) => (
            <div key={v.id}>
              <strong>{v.razonSocial}</strong> — vencida el {fecha(v.fechaProximaEvaluacion)}
              {' '}({v.diasVencida} días)
            </div>
          ))}
        </Alert>
      )}

      <Group align="flex-end">
        <TextInput
          placeholder="Buscar por número, proveedor o RUC"
          leftSection={<IconSearch size={16} />}
          value={texto}
          onChange={(e) => { setTexto(e.currentTarget.value); setPagina(1) }}
          w={340}
        />
        <Select placeholder="Ambos formatos" clearable w={210}
          data={TIPOS_FICHA.map((t) => ({ value: t, label: ETIQUETA_FICHA[t] }))}
          value={tipo} onChange={(v) => { setTipo(v); setPagina(1) }} />
        <Select placeholder="Todos los estados" clearable w={180}
          data={ESTADOS.map((e) => ({ value: e, label: e }))}
          value={estado} onChange={(v) => { setEstado(v); setPagina(1) }} />
      </Group>

      <DataTable
        withTableBorder borderRadius="sm" striped highlightOnHover minHeight={220}
        fetching={isFetching}
        records={data?.datos ?? []}
        totalRecords={data?.total ?? 0}
        recordsPerPage={POR_PAGINA}
        page={pagina}
        onPageChange={setPagina}
        noRecordsText="No hay fichas emitidas"
        idAccessor="id"
        columns={[
          {
            accessor: 'numero', title: 'Número', width: 150,
            render: (f) => <span style={{ fontFamily: 'monospace' }}>{f.numero}</span>,
          },
          {
            accessor: 'tipo', title: 'Formato', width: 150,
            render: (f) => (
              <Badge size="sm" variant="light"
                color={f.tipo === 'SELECCION_PROVEEDOR' ? 'indigo' : 'grape'}>
                {f.tipo === 'SELECCION_PROVEEDOR' ? 'Selección' : 'Evaluación'}
              </Badge>
            ),
          },
          { accessor: 'proveedor', title: 'Proveedor', width: '26%' },
          {
            accessor: 'puntajePromedio', title: 'Puntaje', width: 90, textAlign: 'right',
            render: (f) => (
              <span style={{ fontFamily: 'monospace' }}>
                {f.puntajePromedio === null ? '—' : f.puntajePromedio.toFixed(2)}
              </span>
            ),
          },
          {
            accessor: 'condicion', title: 'Condición', width: 175,
            render: (f) =>
              f.condicion ? (
                <Badge size="sm" variant="light" color={COLOR_CONDICION[f.condicion]}>
                  {f.condicion}
                </Badge>
              ) : '—',
          },
          {
            accessor: 'fechaProximaEvaluacion', title: 'Próxima', width: 130,
            render: (f) =>
              f.fechaProximaEvaluacion
                ? `${fecha(f.fechaProximaEvaluacion)}${f.periodicidad ? ` · ${f.periodicidad.toLowerCase()}` : ''}`
                : '—',
          },
          { accessor: 'estado', title: 'Estado', width: 115, render: (f) => <EstadoBadge estado={f.estado} /> },
          {
            accessor: 'acciones', title: '', width: 92,
            render: (f) => (
              <Group gap={2} wrap="nowrap">
                <Tooltip label="Adjuntos e historial">
                  <ActionIcon variant="subtle" onClick={() => setPanel(f)}>
                    <IconPaperclip size={16} />
                  </ActionIcon>
                </Tooltip>
                {puedeEmitir && esEditableEstado(f.estado) && (
                  <Tooltip label="Editar">
                    <ActionIcon variant="subtle" onClick={() => void abrir(f)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <PanelDocumento
        documentoId={panel?.id ?? null}
        numero={panel?.numero ?? ''}
        estado={panel?.estado ?? 'Pendiente'}
        puedeEditar={puedeEmitir}
        onCerrar={() => setPanel(null)}
      />

      <Modal
        opened={abierto} onClose={modal.close}
        title={editando ? `Editar ${editando.numero}` : `Nueva ${ETIQUETA_FICHA[tipoNueva].toLowerCase()}`}
        size="80rem"
      >
        <FormularioFicha
          inicial={editando}
          tipoInicial={tipoNueva}
          guardando={guardar.isPending}
          onGuardar={(v) => guardar.mutate(v)}
          onCancelar={modal.close}
        />
      </Modal>
    </Stack>
  )
}
